/**
 * Canonical error display helpers.
 *
 * Maps `ApiError` codes (and raw values) to user-facing copy via i18n keys,
 * and formats operator-facing surfaces like support IDs. Every UI surface
 * that renders an `ApiError` should go through these helpers instead of
 * ad-hoc string checks.
 *
 * Rendering policy:
 *
 *   validation_failed       → i18n title + inline form field details
 *   unauthorized / forbidden → auth redirect or permission toast
 *   not_found               → empty/table state with clear message
 *   conflict / idempotency_conflict → toast with details
 *   rate_limited / quota_exceeded → toast with retry/usage hint
 *   internal_error / readiness_check_failed → toast + support ID
 *   network_error           → toast with connection hint
 */

import { classifyConflict, isStaleVersionMessage } from "./conflicts";
import type { ApiErrorCode } from "./errors";
import {
  ApiError,
  isAuth,
  isConflict,
  isNotFound,
  isQuotaExceeded,
  isRateLimited,
  isReadiness,
  isValidation,
} from "./errors";

/**
 * The minimal translate-function shape this module needs — deliberately
 * narrower than i18next's TFunction overload set, so this module
 * stays testable without booting i18next and doesn't couple to a specific
 * i18n library. i18next's real `t` satisfies this shape.
 */
export type TranslateFn = (key: string, params?: Record<string, unknown>) => string;

// ── i18n key mapping ────────────────────────────────────────────────────────

/** Map a canonical error code to its i18n title key. */
const CODE_TO_TITLE_KEY: Record<ApiErrorCode, string> = {
  validation_failed: "errors.validationFailed",
  unauthorized: "errors.unauthorized",
  forbidden: "errors.forbidden",
  not_found: "errors.notFound",
  conflict: "errors.conflict",
  idempotency_conflict: "errors.idempotencyConflict",
  quota_exceeded: "errors.quotaExceeded",
  rate_limited: "errors.rateLimited",
  readiness_check_failed: "errors.readinessCheckFailed",
  internal_error: "errors.internalError",
  network_error: "errors.networkError",
};

// ── Public helpers ──────────────────────────────────────────────────────────

/**
 * Resolve the error code from any value — an `ApiError`, a raw envelope-like
 * object, or an unknown caught error.
 */
function resolveCode(error: unknown): ApiErrorCode | undefined {
  if (error instanceof ApiError) return error.code;
  if (error && typeof error === "object") {
    const candidate = (error as Record<string, unknown>).code;
    if (typeof candidate === "string") return candidate as ApiErrorCode;
  }
  return undefined;
}

/**
 * Resolve the request ID from any value.
 */
function resolveRequestId(error: unknown): string | undefined {
  if (error instanceof ApiError) return error.requestId;
  if (error && typeof error === "object") {
    const candidate = (error as Record<string, unknown>).request_id;
    if (typeof candidate === "string" && candidate.trim()) return candidate.trim();
    const header = (error as Record<string, unknown>).requestId;
    if (typeof header === "string" && header.trim()) return header.trim();
  }
  return undefined;
}

/**
 * Get the user-facing title for an error.
 *
 * The **frontend owns** all user-facing copy for known canonical error codes.
 * When the backend sends a recognised code, the i18n translation is used
 * regardless of whether the backend also sent a `message`. This keeps
 * localization in one place and prevents the backend from silently
 * overriding user-visible text.
 *
 * Fallback order:
 *   1. Known canonical code → `t(CODE_TO_TITLE_KEY[code])`
 *   2. `ApiError` with a non-empty `message` but **no** known code → backend
 *      message (safe fallback for unexpected codes).
 *   3. Neither code nor message → `t("errors.requestFailed")`.
 */
function isStaleConflict(error: ApiError): boolean {
  const msg = (error.message || "").trim();
  const reason = (error.details as { reason?: unknown } | undefined)?.reason;
  if (reason === "stale_manifest_revision") return false;
  return !msg || reason === "stale_version" || reason === "stale_revision" || isStaleVersionMessage(msg);
}

// Resource kinds and referencing tables/markers the backend reports in a
// conflict's details. Anything else (raw ids, unknown tables) is never shown.
const KNOWN_RESOURCES = [
  "node",
  "package",
  "global_node_group",
  "external_subscription",
  "adapter_agent",
  "user",
  "user_credential",
] as const;

const KNOWN_REFERENCES = [
  "subscriptions",
  "subscription_package_snapshots",
  "boot_sessions",
  "catalog_admin_node_selections",
  "global_node_group_members",
  "tenant_node_group_members",
  "subscription_link_nodes",
  "subscription_package_snapshot_nodes",
  "managed_users",
  "tenant_distribution_packages",
  "tenant_admin_role",
  "node_agent_assignments",
  "package_external_subscriptions",
  "subscription_package_snapshot_external_subscriptions",
  "user_roles",
  "user_management_assignments",
  "user_credentials",
  "traffic_routing_policies",
  "user_routing_policies",
  "nodes",
  "billing_subscription_locks",
  "grants",
  "referenced_data",
] as const;

function isKnownResource(value: string): value is (typeof KNOWN_RESOURCES)[number] {
  return (KNOWN_RESOURCES as readonly string[]).includes(value);
}

/** Translated, de-duplicated labels for the known references in `referenced_by`. */
export function referenceLabels(refs: unknown, t: TranslateFn): string[] {
  if (!Array.isArray(refs)) return [];
  const labels = refs
    .map(String)
    .filter((ref) => (KNOWN_REFERENCES as readonly string[]).includes(ref))
    .map((ref) => t(`errors.references.${ref}`));
  return [...new Set(labels)];
}

export function errorTitle(error: unknown, t: TranslateFn): string {
  // A retry refused because the server no longer keeps the first attempt.
  if (error instanceof Error && error.name === "ReplayExpiredError") return t("common.lastUnknownExpired");
  const code = resolveCode(error);

  // Conflicts: frontend copy built from the structured details. The backend
  // message (English, written for logs) is only ever the secondary line, via
  // errorDescription. Stale-ETag conflicts keep the generic "changed while you
  // were editing" copy.
  if (
    code === "conflict" &&
    error instanceof ApiError &&
    (error.details as { reason?: unknown } | undefined)?.reason === "stale_manifest_revision"
  ) {
    return t("errors.staleManifest");
  }
  // Refused before sending: a token may exist for this node that was never
  // shown (see unseenTokens.refuseWhileUnseen). Not a concurrent edit.
  if (
    code === "conflict" &&
    error instanceof ApiError &&
    (error.details as { reason?: unknown } | undefined)?.reason === "unseen_token_hold"
  ) {
    return t("nodes.unseenTokenReissueOff");
  }
  // Disabling a server whose latest manifest isn't applied yet.
  if (
    code === "conflict" &&
    error instanceof ApiError &&
    (error.details as { reason?: unknown } | undefined)?.reason === "manifest_apply_pending"
  ) {
    return t("servers.disable.manifestPending");
  }
  if (code === "conflict" && error instanceof ApiError) {
    const kind = classifyConflict(error);
    if (kind === "lastTenantAdmin") return t("errors.lastTenantAdmin");
    if (kind === "packageNeedsRefresh") return t("errors.packageNeedsRefresh");
  }
  if (code === "conflict" && error instanceof ApiError && !isStaleConflict(error)) {
    const details = (error.details ?? {}) as { resource?: unknown; referenced_by?: unknown };
    const resource =
      typeof details.resource === "string" && isKnownResource(details.resource)
        ? t(`errors.resources.${details.resource}`)
        : null;
    const refs = referenceLabels(details.referenced_by, t);
    if (resource && refs.length) {
      return t("errors.conflictInUse", { resource, refs: refs.join(t("errors.listSeparator")) });
    }
    if (refs.length) return t("errors.conflictInUseAny", { refs: refs.join(t("errors.listSeparator")) });
    if (resource) return t("errors.conflictResource", { resource });
    return t("errors.conflictGeneric");
  }

  // Frontend-owned i18n copy for every known canonical code.
  if (code && code in CODE_TO_TITLE_KEY) {
    return t(CODE_TO_TITLE_KEY[code]);
  }

  // Backend message is a safe fallback only for unknown / unexpected codes.
  if (error instanceof ApiError && error.message) {
    return error.message;
  }

  return t("errors.requestFailed");
}

/**
 * Get the user-facing description for an error.
 *
 * Currently this is empty for most codes — the title carries the full copy.
 * Future per-field validation details would go here.
 */
export function errorDescription(error: unknown, _t: TranslateFn): string | undefined {
  // The backend's own wording, as a secondary line under a recognized
  // conflict's frontend copy (e.g. "tenant package name already exists").
  if (error instanceof ApiError && resolveCode(error) === "conflict" && !isStaleConflict(error)) {
    // Fully explained by translated copy; the English log line adds nothing.
    const kind = classifyConflict(error);
    if (kind === "lastTenantAdmin" || kind === "packageNeedsRefresh") return undefined;
    if ((error.details as { reason?: unknown } | undefined)?.reason === "manifest_apply_pending")
      return undefined;
    const msg = (error.message || "").trim();
    return msg || undefined;
  }
  return undefined;
}

/**
 * Format the support ID line for operator/debug surfaces.
 *
 * Returns `undefined` when no request ID is available, so the caller
 * can conditionally render the collapsible section.
 */
export function formatSupportId(error: unknown, t: TranslateFn): string | undefined {
  const requestId = resolveRequestId(error);
  if (!requestId) return undefined;
  return t("errors.supportId", { id: requestId });
}

/**
 * Check whether an error represents a not-found state that should
 * render as an empty table state rather than a toast.
 */
export function isNotFoundState(error: unknown): boolean {
  return isNotFound(error);
}

/**
 * Check whether an error should suppress the default toast. Only a lost
 * session (401) is handled elsewhere (redirect to sign-in); a 403 means the
 * action was refused and the user must be told.
 */
export function isSuppressedToast(error: unknown): boolean {
  return isAuth(error) && !isForbidden(error);
}

function isForbidden(error: unknown): boolean {
  if (error instanceof ApiError) return error.code === "forbidden" || error.status === 403;
  return !!error && typeof error === "object" && (error as { code?: unknown }).code === "forbidden";
}

/**
 * Check whether an error should show a retry prompt in the table.
 */
export function isRetryable(error: unknown): boolean {
  return isRateLimited(error) || isReadiness(error);
}

/**
 * Check whether an error should show a quota hint.
 */
export function isQuotaError(error: unknown): boolean {
  return isQuotaExceeded(error);
}

/**
 * Check whether an error should show as a conflict message.
 */
export function isConflictError(error: unknown): boolean {
  return isConflict(error);
}

/**
 * Check whether an error is a validation error.
 */
export function isValidationError(error: unknown): boolean {
  return isValidation(error);
}

/** Adapt i18next's heavily-overloaded `t` to the narrow TranslateFn shape. */
export function asTranslateFn(t: (...args: never[]) => unknown): TranslateFn {
  return (key, params) => String((t as (k: string, p?: Record<string, unknown>) => unknown)(key, params));
}
